<div class="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/3 md:p-6">
  <div class="flex items-center justify-between">
    <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">
      Sync Status: {{ source | uppercase }} &rarr; {{ destination | uppercase }}
    </h3>

    <div class="relative inline-block">
      <button type="button" class="dropdown-toggle text-gray-400 hover:text-gray-700 dark:hover:text-gray-300" (click)="toggleDropdown()">
        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="size-6"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.2441 6C10.2441 5.0335 11.0276 4.25 11.9941 4.25H12.0041C12.9706 4.25 13.7541 5.0335 13.7541 6C13.7541 6.9665 12.9706 7.75 12.0041 7.75H11.9941C11.0276 7.75 10.2441 6.9665 10.2441 6ZM10.2441 18C10.2441 17.0335 11.0276 16.25 11.9941 16.25H12.0041C12.9706 16.25 13.7541 17.0335 13.7541 18C13.7541 18.9665 12.9706 19.75 12.0041 19.75H11.9941C11.0276 19.75 10.2441 18.9665 10.2441 18ZM11.9941 10.25C11.0276 10.25 10.2441 11.0335 10.2441 12C10.2441 12.9665 11.0276 13.75 11.9941 13.75H12.0041C12.9706 13.75 13.7541 12.9665 13.7541 12C13.7541 11.0335 12.9706 10.25 12.0041 10.25H11.9941Z" fill="currentColor"></path></svg>
      </button>

      <app-dropdown [isOpen]="dropdownIsOpen" (close)="closeDropdown()" className="w-36 p-2">
        <app-dropdown-item (itemClick)="refresh()"
          className="flex items-center gap-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          Refresh
        </app-dropdown-item>
      </app-dropdown>
    </div>
  </div>

  @if (loading()) {
    <div class="flex h-45 items-center justify-center">
      <svg class="h-6 w-6 animate-spin text-blue-500" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
    </div>
  } @else if (error()) {
    <p class="mt-4 rounded-lg border border-red-200 bg-red-100 p-3 text-sm text-red-600">{{ error() }}</p>
  } @else if (organizationId) {
    <!-- ══════════ Single organization ══════════ -->
    @if (selectedOrganization; as org) {
      <div class="mt-4 flex flex-wrap items-center gap-2">
        <span class="h-2 w-2 shrink-0 rounded-full"
          [class.bg-red-500]="!!org.error" [class.bg-orange-500]="org.hasDelta && !org.error" [class.bg-green-500]="!org.hasDelta && !org.error"></span>
        <span class="text-sm font-medium text-gray-800 dark:text-white/90">{{ org.organizationName }}</span>
        <span class="rounded-full px-2 py-0.5 text-xs font-medium"
          [class.bg-red-100]="!!org.error" [class.text-red-700]="!!org.error"
          [class.bg-orange-100]="org.hasDelta && !org.error" [class.text-orange-700]="org.hasDelta && !org.error"
          [class.bg-green-100]="!org.hasDelta && !org.error" [class.text-green-700]="!org.hasDelta && !org.error">
          {{ org.error ? 'Failed' : (org.hasDelta ? 'With Delta' : 'In Sync') }}
        </span>
      </div>

      @if (org.hasDelta && !org.error) {
        <div class="mt-5 border-t border-gray-100 pt-5 dark:border-gray-800">
          <p class="mb-3 text-xs font-medium text-gray-500 dark:text-gray-400">
            Delta Detail — {{ areaBreakdownLabel }}
          </p>
          <div class="max-w-xs">
            <ng-container *ngTemplateOutlet="orgDeltaCard; context: { org: org }"></ng-container>
          </div>
        </div>
      }
    } @else {
      <p class="mt-4 text-sm text-gray-400">No delta data available for this organization.</p>
    }
  } @else {
    <!-- ══════════ All organizations ══════════ -->
    <div class="mt-4 flex flex-col items-center gap-4 sm:flex-row">
      <div class="w-full max-w-40 shrink-0">
        <apx-chart
          [series]="series"
          [chart]="chart"
          [colors]="colors"
          [labels]="labels"
          [dataLabels]="dataLabels"
          [stroke]="stroke"
          [legend]="legend"
          [plotOptions]="plotOptions"
        ></apx-chart>
      </div>
      <div class="w-full flex-1 space-y-2">
        <div class="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2 dark:border-gray-800">
          <span class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <span class="h-2.5 w-2.5 rounded-full bg-[#12B76A]"></span>
            In Sync
          </span>
          <span class="text-sm font-semibold text-gray-800 dark:text-white/90">{{ inSyncCount() }}</span>
        </div>
        <div class="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2 dark:border-gray-800">
          <span class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <span class="h-2.5 w-2.5 rounded-full bg-[#F79009]"></span>
            With Delta
          </span>
          <span class="text-sm font-semibold text-gray-800 dark:text-white/90">{{ withDeltaCount() }}</span>
        </div>
        @if (skippedCount() > 0 || failedCount() > 0) {
          <p class="pt-1 text-xs text-gray-400">
            @if (skippedCount() > 0) { {{ skippedCount() }} skipped (obsolete) }
            @if (skippedCount() > 0 && failedCount() > 0) { &bull; }
            @if (failedCount() > 0) { {{ failedCount() }} failed to analyse }
          </p>
        }
      </div>
    </div>

    @if (topDeltaOrganizations.length > 0) {
      <div class="mt-6 border-t border-gray-100 pt-5 dark:border-gray-800">
        <p class="mb-3 text-xs font-medium text-gray-500 dark:text-gray-400">
          Delta Detail — {{ areaBreakdownLabel }} per organization, ranked by most changes first
        </p>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          @for (org of topDeltaOrganizations; track org.organizationId) {
            <ng-container *ngTemplateOutlet="orgDeltaCard; context: { org: org }"></ng-container>
          }
        </div>
      </div>
    }
  }
</div>

<!-- Shared per-organization bars-with-axis card, used by both the single-organization view above
     and the top-organizations grid in the all-organizations view. -->
<ng-template #orgDeltaCard let-org="org">
  <div class="rounded-lg border border-gray-100 p-3 dark:border-gray-800">
    <p class="mb-2 truncate text-sm font-semibold text-gray-800 dark:text-white/90" [title]="org.organizationName">{{ org.organizationName }}</p>
    <div class="mb-3 flex flex-wrap items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400">
      <span class="flex items-center gap-1"><span class="h-1.5 w-1.5 rounded-full bg-[#12B76A]"></span>Added</span>
      <span class="flex items-center gap-1"><span class="h-1.5 w-1.5 rounded-full bg-[#F04438]"></span>Removed</span>
      <span class="flex items-center gap-1"><span class="h-1.5 w-1.5 rounded-full bg-[#F79009]"></span>Modified</span>
      <span class="flex items-center gap-1"><span class="h-1.5 w-1.5 rounded-full bg-gray-300"></span>Aligned</span>
    </div>
    <div class="flex items-end gap-2">
      <!-- axis -->
      <div class="flex h-28 flex-col justify-between text-[10px] text-gray-400">
        @for (tick of axisTicks(orgChartMax(org)); track tick) {
          <span>{{ tick }}</span>
        }
      </div>
      <!-- bars -->
      <div class="flex h-28 flex-1 items-end justify-around gap-2">
        @for (areaKey of presentAreas; track areaKey) {
          @let detail = getArea(org, areaKey);
          <div class="flex h-full w-full flex-col items-center justify-end">
            @if (detail && detail.hasDelta) {
              <div class="flex w-full max-w-8 flex-col-reverse overflow-hidden rounded-t"
                [style.height.%]="segmentHeightPercent(detail.changes, orgChartMax(org))">
                @if (detail.added > 0) {
                  <div class="flex w-full items-center justify-center bg-[#12B76A] text-[10px] font-semibold text-white"
                    [style.height.%]="(detail.added / detail.changes) * 100">{{ detail.added }}</div>
                }
                @if (detail.removed > 0) {
                  <div class="flex w-full items-center justify-center bg-[#F04438] text-[10px] font-semibold text-white"
                    [style.height.%]="(detail.removed / detail.changes) * 100">{{ detail.removed }}</div>
                }
                @if (detail.modified > 0) {
                  <div class="flex w-full items-center justify-center bg-[#F79009] text-[10px] font-semibold text-white"
                    [style.height.%]="(detail.modified / detail.changes) * 100">{{ detail.modified }}</div>
                }
              </div>
            } @else {
              <div class="flex h-6 w-full max-w-8 items-center justify-center rounded bg-gray-100 text-gray-400 dark:bg-gray-800">
                <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
            }
            <span class="mt-1 text-[10px] text-gray-500 dark:text-gray-400">{{ areaLabels[areaKey] }}</span>
          </div>
        }
      </div>
    </div>
  </div>
</ng-template>
